import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type Book, type ChapterDescription, type MemberProfile, type RatingsResponse } from "../api";
import { Avatar } from "../components/Avatar";
import { Badge, Button, Card, EmptyState, Ornament } from "../components/ui";
import { BookCover } from "../components/BookCover";
import { BookSections } from "../components/BookSections";
import { ReadingProgress } from "../components/ReadingProgress";
import { BookRatings } from "../components/BookRatings";
import { StarRating } from "../components/StarRating";
import { GenerateAllButton } from "../components/GenerateAllButton";
import { PickerEditor } from "../components/PickerEditor";
import { SectionNav, SectionIcons, type SectionNavItem } from "../components/SectionNav";

const BOOK_SECTIONS: SectionNavItem[] = [
  { id: "section-top", label: "Top of page", icon: SectionIcons.hero },
  { id: "section-progress", label: "Reading progress", icon: SectionIcons.progress },
  { id: "section-context", label: "Author & moment", icon: SectionIcons.context },
  { id: "section-themes", label: "Themes", icon: SectionIcons.themes },
  { id: "section-passages", label: "Key passages", icon: SectionIcons.passages },
  { id: "section-chapters", label: "Chapter walk-through", icon: SectionIcons.chapters },
  { id: "section-notes", label: "Chapter notes", icon: SectionIcons.notes },
  { id: "section-questions", label: "Discussion questions", icon: SectionIcons.questions },
  { id: "section-characters", label: "Characters", icon: SectionIcons.characters },
  { id: "section-relationships", label: "Constellations", icon: SectionIcons.relationships },
  { id: "section-venue", label: "Venue", icon: SectionIcons.venue },
  { id: "section-merch", label: "Merch", icon: SectionIcons.merch },
  { id: "section-pairings", label: "Food & drink", icon: SectionIcons.pairings },
  { id: "section-ratings", label: "Ratings & reviews", icon: SectionIcons.ratings },
];

export default function HomePage() {
  const current = useQuery({
    queryKey: ["current-book"],
    queryFn: () => api.get<{ book: Book | null }>("/books/current"),
  });

  const book = current.data?.book ?? null;

  // For the chapter count in the reading progress widget.
  const chaptersCache = useQuery({
    queryKey: ["ai", book?.id ?? 0, "chapters"],
    queryFn: () =>
      api.get<{ content: { type: "chapters"; data: ChapterDescription[]; generatedAt: string } | null }>(
        `/ai/${book!.id}/chapters`
      ),
    enabled: !!book,
  });

  const members = useQuery({
    queryKey: ["members"],
    queryFn: () => api.get<{ members: MemberProfile[] }>("/members"),
    enabled: !!book,
  });

  const ratings = useQuery({
    queryKey: ["ratings", book?.id ?? 0],
    queryFn: () => api.get<RatingsResponse>(`/ratings/${book!.id}`),
    enabled: !!book,
  });

  if (current.isLoading) {
    return <div className="text-plum-soft">Loading…</div>;
  }

  if (!book) {
    return (
      <Card accent>
        <EmptyState
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 4.5C5 3.67 5.67 3 6.5 3h11c.83 0 1.5.67 1.5 1.5v15.5h-12.5c-.83 0-1.5-.67-1.5-1.5V4.5z"
                stroke="currentColor"
                strokeWidth="1.2"
              />
              <path d="M9 8h6M9 11h6M9 14h4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
          }
          title="No book yet"
          description="The club hasn't picked a book to discuss. Choose one and we'll prepare your meeting kit."
          action={
            <Link to="/add-book">
              <Button>Add a book</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const picker = members.data?.members.find((m) => m.id === book.addedByUserId);
  const chaptersData = chaptersCache.data?.content?.data ?? null;

  return (
    <div className="flex flex-col gap-12">
      <SectionNav sections={BOOK_SECTIONS} />
      {/* Hero */}
      <section id="section-top" className="relative scroll-mt-24">
        <div className="absolute -top-6 -left-6 -right-6 -bottom-6 bg-gradient-to-br from-blush-soft/30 via-cream to-cream rounded-3xl -z-10" />
        <div className="flex flex-col md:flex-row gap-10 items-center md:items-start py-4">
          <BookCover url={book.coverUrl} title={book.title} author={book.author} size="xl" />
          <div className="flex-1 flex flex-col gap-4 text-center md:text-left">
            <Badge tone="gold">Currently Reading</Badge>
            <h1 className="font-serif text-4xl sm:text-5xl text-plum leading-[1.05] tracking-tight">
              {book.title}
            </h1>
            <p className="font-serif italic text-xl text-plum-soft">by {book.author}</p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start items-center text-sm text-plum-soft">
              {book.pageCount ? <span>{book.pageCount} pages</span> : null}
              {book.isbn ? (
                <>
                  <span className="text-plum-mute">·</span>
                  <span className="font-mono text-xs tracking-wide">{book.isbn}</span>
                </>
              ) : null}
              <AudibleButton title={book.title} author={book.author} />
            </div>

            <div className="flex items-center gap-5 flex-wrap justify-center md:justify-start mt-1">
              <PickerEditor
                bookId={book.id}
                picker={picker}
                members={members.data?.members ?? []}
              />
              {ratings.data?.average ? (
                <div className="flex items-center gap-2">
                  <StarRating value={ratings.data.average} readOnly size={16} />
                  <span className="text-xs text-plum-mute">
                    {ratings.data.average.toFixed(1)} · {ratings.data.count}
                  </span>
                </div>
              ) : null}
            </div>

            {book.summary ? (
              <p className="text-plum-soft leading-relaxed mt-2 max-w-2xl">{book.summary}</p>
            ) : null}
            <div className="flex gap-3 justify-center md:justify-start mt-4 flex-wrap">
              <GenerateAllButton bookId={book.id} variant="primary" size="sm" />
              <Link to="/add-book">
                <Button variant="ghost" size="sm">Choose a different book</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="section-progress" className="scroll-mt-24">
        <ReadingProgress bookId={book.id} totalChapters={chaptersData?.length ?? null} />
      </section>

      <Ornament />

      <BookSections bookId={book.id} />

      <section id="section-ratings" className="scroll-mt-24">
        <BookRatings bookId={book.id} />
      </section>
    </div>
  );
}

// Tiny inline Audible search button — sits in the book's metadata strip
// next to page count + ISBN. Replaces the standalone "Listen along" card.
function AudibleButton({ title, author }: { title: string; author: string }) {
  const q = encodeURIComponent(`${title} ${author}`);
  return (
    <>
      <span className="text-plum-mute">·</span>
      <a
        href={`https://www.audible.com/search?keywords=${q}`}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-bordeaux text-bordeaux hover:bg-bordeaux hover:text-rose-paper font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
        title="Search this book on Audible"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M3 18v-7a9 9 0 1 1 18 0v7M3 18a2 2 0 0 0 2 2h2v-7H5a2 2 0 0 0-2 2zM21 18a2 2 0 0 1-2 2h-2v-7h2a2 2 0 0 1 2 2z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
        Audible
      </a>
    </>
  );
}
